/**
 * Lighting for a DARK, warmly-lit casino at night — lit ENTIRELY by explicit
 * lights, NO HDRI environment.
 *
 * Why no <Environment>: an HDRI used as image-based lighting floods the whole
 * scene the moment it finishes loading. Because it loads ASYNCHRONOUSLY, the
 * scene was dark on a cold load (HDRI not arrived yet) and washed-out pale on a
 * warm reload (HDRI cached → applies instantly and floods). That timing-dependent
 * flip was the "first load dark, reload pale" bug, and it overpowered tone mapping
 * and exposure alike. Dropping the HDRI makes the lighting fully synchronous and
 * therefore DETERMINISTIC — identical on every load.
 *
 * The look: a tight warm overhead SPOTLIGHT pools on the felt (casino-pendant
 * lamp), low warm fills keep the room from going pure black, and a back-wall wash
 * lets the room read behind the dealer. Gold elements still glow via emissive +
 * selective bloom. Pairs with ACESFilmic tone mapping + 0.62 exposure on the
 * canvas (App.tsx) and the ToneMapping pass in the composer (Effects.tsx).
 *
 * (If subtle HDRI reflections on the gold are ever wanted back, add a SINGLE
 * <Environment> with a genuinely low, verified intensity — but only after
 * confirming it doesn't reintroduce the load-timing flood.)
 */

import { palette } from '../theme/tokens';
import { TABLE_CENTER } from './seating';

export function Lighting() {
  const [tx, , tz] = [TABLE_CENTER.x, TABLE_CENTER.y, TABLE_CENTER.z];

  return (
    <>
      {/* Very low warm ambient so the darkest surroundings never go fully black. */}
      <ambientLight intensity={0.08} color={'#5a4a2a'} />

      {/* ---- The casino-pendant KEY: a tight warm spotlight pooling on the felt.
           Single shadow-caster (1024px map, per budget). Tightened + dimmed so the
           cone pools on the FELT, not a bright disc on the carpet. ---- */}
      <spotLight
        castShadow
        position={[tx + 0.6, 6.2, tz + 1.2]}
        target-position={[tx, 0.95, tz]}
        angle={0.5}
        penumbra={0.85}
        intensity={80}
        distance={16}
        decay={2}
        color={'#ffe6b0'}
        shadow-mapSize={[1024, 1024]}
        shadow-bias={-0.0004}
        shadow-camera-near={1}
        shadow-camera-far={16}
      />

      {/* Soft warm fill from the front-left (no shadow) so player hands aren't black.
          Kept low so it doesn't wash the carpet/walls to pale orange. */}
      <pointLight position={[-3.2, 2.4, tz + 2.6]} intensity={4} color={palette.gold2} distance={9} decay={2} />

      {/* Cool-ish rim/edge accent from behind to separate the table from the dark. */}
      <pointLight position={[2.8, 2.6, tz - 2.2]} intensity={4} color={'#cfe0ff'} distance={9} decay={2} />

      {/* Back-wall / High Rollers sign wash so the room reads behind the dealer
          (the sign also self-illuminates via emissive bloom). Low — just enough. */}
      <pointLight position={[0, 3.4, -3.6]} intensity={4} color={'#ffd9a0'} distance={8} decay={2} />

      {/* Dealer-station light: a soft warm fill IN FRONT of the croupier (toward
          the camera) so the dealer + tuxedo read — they sit behind the felt,
          outside the pendant's pool. Limited distance so it doesn't spill onto
          the player felt. */}
      <pointLight position={[0, 2.9, -1.5]} intensity={7} color={'#ffe6b0'} distance={5} decay={2} />
    </>
  );
}
